import { useMemo, useState } from 'react';
import {
  Archive,
  CheckCircle,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  FolderOpen,
  GraduationCap,
  Home,
  Shield,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import WorkerPremiumCard, { WorkerPremiumCardHeader } from '../worker/WorkerPremiumCard';
import InlineDocumentViewer from '../shared/InlineDocumentViewer';
import { canViewDocument, resolveDocumentFetchUrl } from '../../lib/resolveDocumentFetchUrl';
import { cn } from '../../lib/utils';

const CATEGORY_META = {
  identity: {
    label: 'Identity & right to work',
    icon: UserCheck,
    accent: 'bg-sky-50 text-sky-700 ring-sky-100',
  },
  checks: {
    label: 'DBS & background checks',
    icon: ShieldCheck,
    accent: 'bg-violet-50 text-violet-700 ring-violet-100',
  },
  address: {
    label: 'Proof of address',
    icon: Home,
    accent: 'bg-amber-50 text-amber-700 ring-amber-100',
  },
  training: {
    label: 'Training & certificates',
    icon: GraduationCap,
    accent: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  },
  other: {
    label: 'Other documents',
    icon: FileText,
    accent: 'bg-slate-50 text-slate-700 ring-slate-100',
  },
};

function resolveCategory(item) {
  const type = String(item?.type || item?.category || '').toLowerCase();
  if (type.includes('training') || item?.source === 'training') return 'training';
  if (type.includes('proof_of_address') || type.includes('address')) return 'address';
  if (type.includes('dbs') || type.includes('background')) return 'checks';
  if (
    type.includes('identity')
    || type.includes('right_to_work')
    || type.includes('passport')
    || type.includes('visa')
  ) {
    return 'identity';
  }
  return 'other';
}

function resolveStatus(item, getDocumentDisplayStatus, getTrainingBadgeMeta, documentWorkflowUi) {
  if (item?.source === 'training') {
    const meta = getTrainingBadgeMeta?.(item) || {};
    const isVerified = meta.badge === 'Verified' || item?.verified;
    return {
      key: isVerified ? 'verified' : 'awaiting_review',
      label: meta.badge || (isVerified ? 'Verified' : 'Awaiting review'),
      className: meta.badgeCls || documentWorkflowUi?.awaiting_review?.className,
      verified: isVerified,
    };
  }

  const statusKey = getDocumentDisplayStatus?.(item) || (item?.verified ? 'verified' : 'awaiting_review');
  const meta = documentWorkflowUi?.[statusKey] || documentWorkflowUi?.awaiting_review;
  return {
    key: statusKey,
    label: meta?.label || 'Awaiting review',
    className: meta?.className || 'bg-amber-100 text-amber-700',
    verified: statusKey === 'verified' || Boolean(item?.verified),
  };
}

function VaultRow({
  item,
  formatDate,
  getDocumentDisplayStatus,
  getTrainingBadgeMeta,
  documentWorkflowUi,
  onPreview,
}) {
  const status = resolveStatus(item, getDocumentDisplayStatus, getTrainingBadgeMeta, documentWorkflowUi);
  const viewable = canViewDocument(item);
  const trainingRecordOnly = item?.source === 'training' && !viewable;

  return (
    <button
      type="button"
      onClick={() => viewable && onPreview(item)}
      disabled={!viewable}
      className={cn(
        'group flex w-full items-center gap-3 rounded-xl border border-slate-100 bg-white p-3 text-left transition',
        viewable ? 'hover:border-primary/20 hover:bg-primary/[0.02] hover:shadow-sm' : 'opacity-70',
      )}
      data-testid={item?.testId || `vault-row-${item?.type || item?.id}`}
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1',
          status.verified ? 'bg-green-50 text-green-600 ring-green-100' : 'bg-slate-50 text-slate-500 ring-slate-100',
        )}
      >
        {status.verified ? <Shield className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800">{item.name}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          {item.uploaded_at ? <span>Uploaded {formatDate(item.uploaded_at)}</span> : null}
          {item.expiry_date ? <span>Expires {formatDate(item.expiry_date)}</span> : null}
          {item.partial ? <span className="text-amber-600">Partial upload</span> : null}
        </div>
        {trainingRecordOnly ? (
          <p className="mt-1 text-xs font-medium text-amber-800">
            Training record only — no certificate file linked. Do not treat as a viewable document.
          </p>
        ) : null}
        {status.verified && item.verified_by_name ? (
          <p className="mt-1 text-xs text-green-700">
            Verified by {item.verified_by_name}
            {item.verified_at ? ` · ${formatDate(item.verified_at)}` : ''}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <Badge className={cn('text-[10px] font-medium', status.className)}>
          {status.verified ? <Shield className="mr-1 h-3 w-3" /> : <Clock className="mr-1 h-3 w-3" />}
          {status.label}
        </Badge>
        {viewable ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary opacity-0 transition group-hover:opacity-100">
            <Eye className="h-3 w-3" />
            Check file
          </span>
        ) : (
          <span className="text-[11px] text-amber-700">
            {trainingRecordOnly ? 'No linked file' : 'File link unavailable'}
          </span>
        )}
      </div>
    </button>
  );
}

export default function SubmittedDocumentsVault({
  documents = [],
  trainings = [],
  token,
  apiBase,
  formatDate,
  getDocumentDisplayStatus,
  getTrainingBadgeMeta,
  documentWorkflowUi,
  className,
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [viewerFetchUrl, setViewerFetchUrl] = useState(null);

  const items = useMemo(() => {
    const docItems = (documents || []).map((doc) => ({ ...doc, source: 'document' }));
    const trainingItems = (trainings || []).map((training) => ({
      id: training.source_document_id || `training-${training.id}`,
      document_id: training.source_document_id || null,
      type: `training_${training.id}`,
      name: training.name,
      source_document_id: training.source_document_id || null,
      uploaded_at: training.completed_at || training.uploaded_at,
      expiry_date: training.expiry_date,
      verified: training.verified,
      status: training.status,
      source: 'training',
      testId: `completed-training-${training.id}`,
    }));
    return [...docItems, ...trainingItems];
  }, [documents, trainings]);

  const stats = useMemo(() => {
    const verified = items.filter((item) => resolveStatus(item, getDocumentDisplayStatus, getTrainingBadgeMeta, documentWorkflowUi).verified).length;
    const awaiting = items.length - verified;
    return { total: items.length, verified, awaiting };
  }, [items, getDocumentDisplayStatus, getTrainingBadgeMeta, documentWorkflowUi]);

  const grouped = useMemo(() => {
    const groups = {};
    items.forEach((item) => {
      const key = resolveCategory(item);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return Object.entries(CATEGORY_META)
      .map(([key, meta]) => ({ key, meta, items: groups[key] || [] }))
      .filter((group) => group.items.length > 0);
  }, [items]);

  const recentItems = useMemo(() => items.slice(0, 3), [items]);

  const openPreview = (item) => {
    const fetchUrl = resolveDocumentFetchUrl(item, { apiBase });
    if (!fetchUrl) return;
    setViewerDoc(item);
    setViewerFetchUrl(fetchUrl);
    setViewerOpen(true);
  };

  if (!items.length) return null;

  return (
    <>
      <WorkerPremiumCard accent="emerald" className={className} data-testid="submitted-documents-vault">
        <WorkerPremiumCardHeader
          icon={Archive}
          title="Document vault"
          description="Submitted records and linked files. A training record does not always include a certificate."
          action={(
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 rounded-full border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              onClick={() => setDrawerOpen(true)}
              data-testid="open-document-vault"
            >
              <FolderOpen className="h-3.5 w-3.5" />
              Browse all
            </Button>
          )}
        />

        <div className="mb-4 grid grid-cols-3 gap-2">
          <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5 text-center">
            <p className="text-lg font-bold text-slate-800">{stats.total}</p>
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Total</p>
          </div>
          <div className="rounded-xl border border-green-100 bg-green-50/80 px-3 py-2.5 text-center">
            <p className="text-lg font-bold text-green-700">{stats.verified}</p>
            <p className="text-[11px] font-medium uppercase tracking-wide text-green-600">Verified</p>
          </div>
          <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-3 py-2.5 text-center">
            <p className="text-lg font-bold text-amber-700">{stats.awaiting}</p>
            <p className="text-[11px] font-medium uppercase tracking-wide text-amber-600">In review</p>
          </div>
        </div>

        <div className="space-y-2">
          {recentItems.map((item) => (
            <VaultRow
              key={`${item.source}-${item.type || item.id}`}
              item={item}
              formatDate={formatDate}
              getDocumentDisplayStatus={getDocumentDisplayStatus}
              getTrainingBadgeMeta={getTrainingBadgeMeta}
              documentWorkflowUi={documentWorkflowUi}
              onPreview={openPreview}
            />
          ))}
        </div>

        {items.length > 3 ? (
          <Button
            variant="ghost"
            className="mt-3 w-full gap-1 text-sm text-slate-600 hover:text-primary"
            onClick={() => setDrawerOpen(true)}
          >
            View all {items.length} documents
            <ChevronRight className="h-4 w-4" />
          </Button>
        ) : null}
      </WorkerPremiumCard>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="flex w-full flex-col overflow-hidden p-0 sm:max-w-[520px]">
          <SheetHeader className="border-b border-slate-100 px-6 py-5 text-left">
            <SheetTitle className="flex items-center gap-2 text-lg font-bold text-[#17324D]">
              <Archive className="h-5 w-5 text-primary" />
              Document vault
            </SheetTitle>
            <SheetDescription className="text-sm text-slate-600">
              {stats.verified} verified · {stats.awaiting} awaiting review · {stats.total} total
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="space-y-6">
              {grouped.map(({ key, meta, items: groupItems }) => {
                const Icon = meta.icon;
                return (
                  <section key={key}>
                    <div className="mb-2 flex items-center gap-2 px-1">
                      <span className={cn('flex h-7 w-7 items-center justify-center rounded-lg ring-1', meta.accent)}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <h3 className="text-sm font-semibold text-slate-800">{meta.label}</h3>
                      <Badge variant="secondary" className="ml-auto text-[10px]">
                        {groupItems.length}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {groupItems.map((item) => (
                        <VaultRow
                          key={`drawer-${item.source}-${item.type || item.id}`}
                          item={item}
                          formatDate={formatDate}
                          getDocumentDisplayStatus={getDocumentDisplayStatus}
                          getTrainingBadgeMeta={getTrainingBadgeMeta}
                          documentWorkflowUi={documentWorkflowUi}
                          onPreview={(doc) => {
                            openPreview(doc);
                            setDrawerOpen(false);
                          }}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>

          <div className="border-t border-slate-100 px-6 py-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
              Verified copies include Osabea review stamps where applicable.
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <InlineDocumentViewer
        open={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setViewerDoc(null);
          setViewerFetchUrl(null);
        }}
        fetchUrl={viewerFetchUrl}
        title={viewerDoc?.name || 'Document'}
        token={token}
        filename={viewerDoc?.file_name || viewerDoc?.name || 'document'}
      />
    </>
  );
}